import { describeMissingEvidenceGuidance, EVIDENCE_ENFORCEMENT_LABELS, OpenSubTask, SubTaskCompletionRefusal, TASK_EVIDENCE_SECTION_LABELS, TaskCompletionRefusal, TaskEvidenceShortfall } from '@jms/model';
import { TaskCompletionDialogKind } from './task.completion.dialog.kind';

export enum TaskCompletionRowTone {
  Plain = 'Plain',
  Attention = 'Attention',
  Unchanged = 'Unchanged',
}

export interface TaskCompletionDialogRow {
  key: string;
  name: string;
  tag?: string;
  detail: string;
  tone: TaskCompletionRowTone;
}

export interface TaskCompletionDialogContent {
  kind: TaskCompletionDialogKind;
  title: string;
  intro?: string;
  rows: TaskCompletionDialogRow[];
  guidance: string;
  optInLabel?: string;
}

/** What dialogs A to D say and list, from the server's refusal only; statuses are named by the tenant's own names. */
export function describeTaskCompletionDialog(kind: TaskCompletionDialogKind, refusal: TaskCompletionRefusal): TaskCompletionDialogContent {

  switch (kind) {
    case TaskCompletionDialogKind.OpenSubTasks: return {
      kind,
      title: 'Close the sub-tasks first',
      rows: refusal.openSubTasks.map((subTask, index) => ({ key: subTaskRowKey(subTask, index), name: subTask.name, detail: subTask.statusName, tone: TaskCompletionRowTone.Plain })),
      guidance: 'Complete each sub-task, or close it with a different status. Then complete this task.',
    };
    case TaskCompletionDialogKind.Override: return {
      kind,
      title: 'Complete this task without the missing evidence?',
      rows: refusal.missingEvidence.map(describeShortfallRow),
      guidance: 'Your name and the missing evidence will be recorded in the activity feed.',
      optInLabel: 'I authorise this task to be completed without the missing evidence',
    };
    case TaskCompletionDialogKind.CompleteWithSubTasks: return describeCompleteWithSubTasks(refusal);
    default: return {
      kind: TaskCompletionDialogKind.MissingEvidence,
      title: 'This task can\'t be completed yet',
      rows: refusal.missingEvidence.map(describeShortfallRow),
      guidance: describeMissingEvidenceGuidance(refusal.missingEvidence),
    };
  }
}

/** Dialog E: the confirmed set was refused before any write, so it can say nothing changed. */
export function describeSubTasksBlockedDialog(refusal: SubTaskCompletionRefusal): TaskCompletionDialogContent {

  const one = refusal.blockedSubTasks.length === 1;

  return {
    kind: TaskCompletionDialogKind.SubTasksBlocked,
    title: 'Nothing was completed',
    intro: one
      ? 'One sub-task can\'t be completed right now, so no task was changed.'
      : `${refusal.blockedSubTasks.length} sub-tasks can't be completed right now, so no task was changed.`,
    rows: refusal.blockedSubTasks.map(blocked => ({ key: blocked.taskId, name: blocked.name, detail: blocked.reason, tone: TaskCompletionRowTone.Attention })),
    guidance: one
      ? 'Resolve this sub-task, then try again. Everything else is exactly as it was.'
      : 'Resolve these sub-tasks, then try again. Everything else is exactly as it was.',
  };
}

// Dialog C: every open sub-task is listed with exactly what it is missing, so the user sees all they authorise.
function describeCompleteWithSubTasks(refusal: TaskCompletionRefusal): TaskCompletionDialogContent {

  const count = refusal.openSubTasks.length;
  const named = refusal.taskName?.trim();

  return {
    kind: TaskCompletionDialogKind.CompleteWithSubTasks,
    title: `Complete this task and its ${count} open ${count === 1 ? 'sub-task' : 'sub-tasks'}?`,
    intro: `Each open sub-task is completed first, then ${named ? named : 'this task'}. Missing evidence is overridden only where it is missing.`,
    rows: [
      { key: 'parent', name: named ? named : 'This task', tag: 'This task', ...describeMissing(refusal.missingEvidence) },
      ...refusal.openSubTasks.map((subTask: OpenSubTask, index) => ({ key: subTaskRowKey(subTask, index), name: subTask.name, ...describeMissing(subTask.missingEvidence ?? []) })),
      ...(refusal.resolvedSubTasks ?? []).map(subTask => ({ key: subTask.taskId, name: subTask.name, detail: `Already ${subTask.statusName}, not changed`, tone: TaskCompletionRowTone.Unchanged })),
    ],
    guidance: 'If any of this work was not done, cancel and close those sub-tasks with a different status instead. Each override is recorded on its own task with your name and the missing evidence.',
    optInLabel: 'I authorise this task and its sub-tasks to be completed without the missing evidence',
  };
}

// Sub-tasks relayed by the Claims proxy carry no task id, so the name, then the position, tells the rows apart.
function subTaskRowKey(subTask: OpenSubTask, index: number): string {

  return subTask.taskId || subTask.name || String(index);
}

function describeMissing(missing: readonly TaskEvidenceShortfall[]): Pick<TaskCompletionDialogRow, 'detail' | 'tone'> {

  return missing.length
    ? { detail: `Missing: ${missing.map(shortfall => TASK_EVIDENCE_SECTION_LABELS[shortfall.kind]).join(', ')}`, tone: TaskCompletionRowTone.Attention }
    : { detail: 'Nothing missing', tone: TaskCompletionRowTone.Plain };
}

function describeShortfallRow(shortfall: TaskEvidenceShortfall): TaskCompletionDialogRow {

  return { key: shortfall.kind, name: TASK_EVIDENCE_SECTION_LABELS[shortfall.kind], detail: EVIDENCE_ENFORCEMENT_LABELS[shortfall.level], tone: TaskCompletionRowTone.Plain };
}
